import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '@/auth/api';
import type {
  Customer,
  CustomerContact,
  CustomerAddress,
  CustomerInvite,
} from '@breaker/shared-types';
import type {
  CreateCustomerBody,
  UpdateCustomerBody,
} from '@breaker/shared-schemas';

interface ListCustomersFilters {
  search?: string;
  /** M2a: only customers whose billing block would block a VAT invoice. */
  billingIncomplete?: boolean;
  limit?: number;
}

export function useCustomers(fleetId: string | null, filters: ListCustomersFilters = {}) {
  const debouncedSearch = useDebouncedValue(filters.search ?? '', 200);
  const apiFilters = {
    ...(debouncedSearch ? { search: debouncedSearch } : {}),
    ...(filters.billingIncomplete ? { billingIncomplete: true } : {}),
    limit: filters.limit ?? 20,
  };

  return useQuery({
    queryKey: ['customers', fleetId ?? '', 'list', apiFilters] as const,
    enabled: Boolean(fleetId),
    queryFn: () =>
      api<{ customers: Customer[] }>(
        `/v1/fleets/${fleetId}/customers`,
        { query: apiFilters },
      ),
    select: (data) => data.customers,
    staleTime: 30_000,
  });
}

export function useCustomer(fleetId: string | null, customerId: string | null) {
  return useQuery({
    queryKey: ['customers', fleetId ?? '', 'detail', customerId ?? ''] as const,
    enabled: Boolean(fleetId && customerId),
    queryFn: () =>
      api<{ customer: Customer }>(
        `/v1/fleets/${fleetId}/customers/${customerId}`,
      ),
    select: (data) => data.customer,
    staleTime: 60_000,
  });
}

export function useCustomerContacts(fleetId: string | null, customerId: string | null) {
  return useQuery({
    queryKey: ['customers', fleetId ?? '', 'contacts', customerId ?? ''] as const,
    enabled: Boolean(fleetId && customerId),
    queryFn: () =>
      api<{ contacts: CustomerContact[] }>(
        `/v1/fleets/${fleetId}/customers/${customerId}/contacts`,
      ),
    select: (data) => data.contacts,
    staleTime: 60_000,
  });
}

export function useCustomerAddresses(fleetId: string | null, customerId: string | null) {
  return useQuery({
    queryKey: ['customers', fleetId ?? '', 'addresses', customerId ?? ''] as const,
    enabled: Boolean(fleetId && customerId),
    queryFn: () =>
      api<{ addresses: CustomerAddress[] }>(
        `/v1/fleets/${fleetId}/customers/${customerId}/addresses`,
      ),
    select: (data) => data.addresses,
    staleTime: 60_000,
  });
}

export function useCreateCustomer(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: CreateCustomerBody) =>
      api<{ customer: Customer }>(
        `/v1/fleets/${fleetId}/customers`,
        { method: 'POST', body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', fleetId ?? ''] });
    },
  });
}

export function useUpdateCustomer(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, body }: { customerId: string; body: UpdateCustomerBody }) =>
      api<{ customer: Customer }>(
        `/v1/fleets/${fleetId}/customers/${customerId}`,
        { method: 'PATCH', body },
      ),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['customers', fleetId ?? ''] });
    },
  });
}

/**
 * Tiny debounce hook so search inputs do not hammer the API on every keystroke.
 * Returns the input value after delayMs of stability.
 */
function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

export function useCreateCustomerAddress(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, body }: { customerId: string; body: Record<string, unknown> }) =>
      api<{ address: CustomerAddress }>(
        `/v1/fleets/${fleetId}/customers/${customerId}/addresses`,
        { method: 'POST', body },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ['customers', fleetId ?? '', 'addresses', vars.customerId],
      });
    },
  });
}

// ============================================================
// Customer portal invites (U6/U8)
// ============================================================

export interface CustomerPortalInviteState {
  state: 'none' | 'pending' | 'accepted';
  pendingExpiresAt: string | null;
  invites: CustomerInvite[];
}

/** The customer's portal-invite state for the Settings/CRM row. */
export function useCustomerPortalInvites(
  fleetId: string | null,
  customerId: string | null,
) {
  return useQuery({
    queryKey: ['customers', fleetId ?? '', 'portal-invites', customerId ?? ''] as const,
    enabled: Boolean(fleetId && customerId),
    queryFn: () =>
      api<CustomerPortalInviteState>(
        `/v1/fleets/${fleetId}/customers/${customerId}/portal-invites`,
      ),
    staleTime: 30_000,
  });
}

/** Mint + email a portal invite for this customer (existing U-mint endpoint). */
export function useMintCustomerPortalInvite(fleetId: string | null) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ customerId, email }: { customerId: string; email: string }) =>
      api<{ invite: { inviteId: string; expiresAt: string; inviteUrl: string } }>(
        `/v1/fleets/${fleetId}/customers/${customerId}/portal-invites`,
        { method: 'POST', body: { email } },
      ),
    onSuccess: (_d, vars) => {
      qc.invalidateQueries({
        queryKey: ['customers', fleetId ?? '', 'portal-invites', vars.customerId],
      });
    },
  });
}
